import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { TfiClose } from "react-icons/tfi";
import axios from "axios";
import ReactECharts from "echarts-for-react";
import { Space, Button } from "antd";
import { api_v3_user_aoe2_info } from "../../../utils/constants";

const Aoe2InfoPopup = ({ setOpenPopup, user, resources = [] }) => {
  const [playerInfo, setPlayerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Llamada a la API para obtener la información del jugador
  useEffect(() => {
    const fetchPlayerInfo = async () => {
      try {
        const response = await axios.get(api_v3_user_aoe2_info(user.playerId));
        setPlayerInfo(response.data);
        setLoading(false);
      } catch (err) {
        setError("Error al cargar la información del jugador");
        setLoading(false);
      }
    };

    fetchPlayerInfo();
  }, [user.playerId]);

  if (loading) {
    return <div>Cargando...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  // Manejo cuando aoe2Info es null
  if (!playerInfo.aoe2Info) {
    return (
      <Wrapper>
        <div className="selector-container">
          <div className="close-icon" onClick={() => setOpenPopup(false)}>
            <TfiClose />
          </div>
          <h3>Aoe2 Info</h3>
          <p className="error">
            No hay información del perfil de AoE2 disponible (ha jugado al menos
            10 partidas recientemente?)
          </p>
          <Space
            size="middle"
            wrap
            style={{
              marginBottom: "16px",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {resources?.map((resource) => (
              <Button
                key={resource.url}
                type="text"
                href={`${resource.url}${user.aoe2ProfileId}`}
                target="_blank"
                icon={
                  <img
                    src={`https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/${resource.icon}`}
                    alt={resource.alt}
                    title={resource.alt}
                    style={{ width: "24px", height: "24px" }}
                  />
                }
              />
            ))}
          </Space>
        </div>
      </Wrapper>
    );
  }

  // Datos del jugador desde la respuesta
  const {
    avgElo,
    hasPlayed50Games,
    hasPlayed10GamesInLast45Days,
    accountOlderThan3Months,
    numberOfGamesInLast45Days,
    olderFirstGameDate,
    numberOfGames,
  } = playerInfo.aoe2Info;

  // Datos para el gráfico - combinar ratings si están bajo "accounts"
  let rawRatings = playerInfo.aoe2Info.ratings || [];
  if (rawRatings.length === 0 && playerInfo.aoe2Info.accounts) {
    playerInfo.aoe2Info.accounts.forEach(acc => {
      if (acc.ratings) {
        rawRatings = rawRatings.concat(acc.ratings);
      }
    });
  }

  const ratings = rawRatings
    .map((rating) => ({
      elo: rating.elo,
      date: new Date(rating.date), // Convertimos a objeto Date para poder ordenar
    }))
    .filter(r => r.elo && !isNaN(r.date.getTime())) // Filtrar datos corruptos
    .sort((a, b) => a.date - b.date); // Ordenamos de la fecha más antigua a la más reciente

  // Opciones para el gráfico de área a gran escala con dataZoom
  const chartOptions = {
    title: {
      text: "Historial de Elo",
      textStyle: { color: "#fff" },
    },
    tooltip: {
      trigger: "axis",
    },
    xAxis: {
      type: "category",
      data: ratings.map((r) => r.date.toLocaleDateString()), // Pasamos las fechas en el orden correcto
      name: "Fecha",
      boundaryGap: false,
      axisLabel: { color: "rgba(255,255,255,0.65)" },
      nameTextStyle: { color: "rgba(255,255,255,0.65)" },
    },
    yAxis: {
      type: "value",
      name: "Elo",
      axisLabel: { color: "rgba(255,255,255,0.65)" },
      nameTextStyle: { color: "rgba(255,255,255,0.65)" },
      splitLine: { lineStyle: { color: "#303030" } },
    },
    series: [
      {
        name: "Elo",
        data: ratings.map((r) => r.elo),
        type: "line",
        areaStyle: { color: "rgba(84, 112, 198, 0.4)" }, // Estilo de área para crear el gráfico de área
        smooth: true, // Suavizar la línea
        color: "#5470c6",
        large: true, // Habilitar procesamiento a gran escala
      },
    ],
    dataZoom: [
      {
        type: "slider", // Control tipo slider en la parte inferior
        start: 0, // Empieza desde el 0% (todo el rango de tiempo visible)
        end: 100, // Hasta el 100% del rango visible
        xAxisIndex: 0,
        textStyle: { color: "#fff" },
        borderColor: "#303030",
        fillerColor: "rgba(84, 112, 198, 0.2)",
      },
      {
        type: "inside", // Permite hacer zoom desplazando con el ratón
        start: 0,
        end: 100,
      },
    ],
  };

  return (
    <Wrapper>
      <div className="selector-container">
        <div className="close-icon" onClick={() => setOpenPopup(false)}>
          <TfiClose />
        </div>
        <h3>Aoe2 Info</h3>
        {avgElo === 0 ? (
          <p className="error">
            Error obteniendo la info. Revisar perfil manualmente
          </p>
        ) : (
          ""
        )}

        <Space
          size="middle"
          wrap
          style={{
            marginBottom: "16px",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {resources?.map((resource) => (
            <Button
              key={resource.url}
              type="text"
              href={`${resource.url}${user.aoe2ProfileId}`}
              target="_blank"
              icon={
                <img
                  src={`https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/${resource.icon}`}
                  alt={resource.alt}
                  title={resource.alt}
                  style={{ width: "24px", height: "24px" }}
                />
              }
            />
          ))}
        </Space>

        <div className="info-container">
          <p>
            <strong>Elo Promedio:</strong> {avgElo}
          </p>
          <p>
            <strong>Ha jugado 50 partidas:</strong>{" "}
            {hasPlayed50Games ? "Sí" : "No"} ({numberOfGames})
          </p>
          <p>
            <strong>Ha jugado 10 partidas en los últimos 45 días:</strong>{" "}
            {hasPlayed10GamesInLast45Days ? "Sí" : "No"} (
            {numberOfGamesInLast45Days} partidas)
          </p>
          <p>
            <strong>Cuenta más antigua de 3 meses:</strong>{" "}
            {accountOlderThan3Months ? "Sí" : "No"} ({olderFirstGameDate})
          </p>
        </div>
        <ReactECharts
          option={chartOptions}
          style={{ height: "400px", width: "100%" }}
        />
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #fff;

  .close-icon {
    display: none; /* Let Ant Design handle the close button */
  }

  .selector-container {
    width: 100%; 
    height: auto;
    background: transparent;
    color: #fff;
    padding: 1rem;
    overflow: auto;

    h3 {
      color: #fff;
    }

    p {
      color: #e6edf3;
    }
    
    strong {
      color: #d4af37;
    }
  }

  .info-container {
    margin-bottom: 2rem;
  }

  .error {
    color: #ff4d4f !important;
  }
`;

export default Aoe2InfoPopup;
